import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { trackPurchaseOnce } from "@/lib/analytics";
import { useCart } from "@/context/CartContext";

const MAX_POLLS = 8;

const CATEGORY_BY_LOOKUP = {
  pw_standard: "net",
  pw_net_entry: "net",
  pw_pro: "net",
  pw_facility: "net",
  pw_hoodie: "apparel",
  pw_hoodie_navy: "apparel",
  pw_hoodie_v2: "apparel",
  pw_backpack: "accessory",
  pw_paddle: "accessory",
};

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [detail, setDetail] = useState(null);
  const { clearCart } = useCart();

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setState("paid");
          clearCart();
          const category = CATEGORY_BY_LOOKUP[data.lookup_key] || "net";
          trackPurchaseOnce({
            transactionId: data.session_id,
            value: data.amount != null ? data.amount / 100 : undefined,
            currency: (data.currency || "usd").toUpperCase(),
            productName: data.label,
            productCategory: category,
            eventName: "shop_checkout_completed",
          });
          trackPurchaseOnce({
            transactionId: data.session_id,
            value: data.amount != null ? data.amount / 100 : undefined,
            currency: (data.currency || "usd").toUpperCase(),
            productName: data.label,
            productCategory: category,
          });
          if (category === "apparel") {
            trackPurchaseOnce({
              transactionId: data.session_id,
              value: data.amount != null ? data.amount / 100 : undefined,
              currency: (data.currency || "usd").toUpperCase(),
              productName: data.label,
              productCategory: category,
              eventName: "merchandise_purchase_completed",
            });
          }
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        if (attempt >= MAX_POLLS) {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId, clearCart]
  );

  useEffect(() => {
    document.title = "Order Confirmed · Pickle Wall™";
    poll(0);
  }, [poll]);

  return (
    <main id="main-content" className="bg-[#0a0a0a] text-white min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-lg glass rounded-3xl p-10 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="text-[#FF6B00] mx-auto animate-spin" />
            <h1 className="mt-6 font-display text-3xl font-black uppercase tracking-tight">
              Confirming Payment
            </h1>
            <p className="mt-3 text-white/60">Hang tight — verifying your order with Stripe.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="text-[#FF6B00] mx-auto" />
            <h1 className="mt-6 font-display text-4xl font-black uppercase tracking-tight" data-testid="payment-success-title">
              You&apos;re In!
            </h1>
            <p className="mt-3 text-white/70">
              Order confirmed{detail?.label ? ` — ${detail.label}` : ""}. A receipt
              is on its way to your email. Welcome to the movement.
            </p>
            {detail?.amount != null && (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#FF6B00]/10 rounded-full">
                <span className="text-xs uppercase tracking-widest text-white/60">Paid</span>
                <span className="font-display font-bold text-[#FF6B00]">
                  ${(detail.amount / 100).toFixed(2)} {(detail.currency || "usd").toUpperCase()}
                </span>
              </div>
            )}
            <Link
              to="/"
              data-testid="payment-success-home"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-[#FF6B00] hover:bg-[#E66000] text-black font-bold uppercase tracking-wider text-sm px-8 py-4 transition-all"
            >
              Back to Home <ArrowRight size={16} />
            </Link>
          </>
        )}

        {(state === "error" || state === "expired") && (
          <>
            <XCircle size={56} className="text-white/40 mx-auto" />
            <h1 className="mt-6 font-display text-3xl font-black uppercase tracking-tight">
              {state === "expired" ? "Session Expired" : "Still Processing"}
            </h1>
            <p className="mt-3 text-white/60">
              {state === "expired"
                ? "This checkout session expired. Please try again."
                : "We couldn't confirm payment yet. If you were charged, your order is safe — check your email or contact us."}
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <Link
                to="/shop"
                className="inline-flex items-center bg-[#FF6B00] hover:bg-[#E66000] text-black font-bold uppercase tracking-wider text-sm px-6 py-3"
              >
                Return to Shop
              </Link>
              <Link
                to="/"
                className="inline-flex items-center glass text-white font-bold uppercase tracking-wider text-sm px-6 py-3"
              >
                Home
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
